/* lib/api/workspaceDocuments.js — боевой клиент базы знаний (порт из
 * frontend/src/lib/api/workspaceDocuments.js). Контракт apiFetch: {ok,status,data}, НЕ бросает.
 * Загрузка — multipart (FormData уходит как есть), резюмирование — ручной fetch+ReadableStream (SSE):
 * EventSource не умеет POST/заголовки, поэтому X-Workspace-Id + CSRF проставляются вручную.
 * Опц. workspaceId → X-Workspace-Id (бизнес-ws из настроек без переключения активного). */
import { apiFetch, getActiveWorkspaceId } from './client.js';

function wsHeaders(workspaceId) {
  if (workspaceId == null) return undefined;
  return { 'X-Workspace-Id': String(workspaceId) };
}

// Ошибка стрима с полями бэкенда: message — текст для пользователя, а code/
// status/state — чтобы экран отличил «доступ к модели закрыт» (402
// subscription_expired: документ сохранён, резюме ждёт тарифа) от сбоя.
function streamError(message, extra) {
  const err = new Error(message);
  if (extra) {
    if (extra.status != null) err.status = extra.status;
    if (extra.detail) err.detail = extra.detail;
    if (extra.code) err.code = extra.code;
    if (extra.state) err.state = extra.state;
  }
  return err;
}

// REST-клиент к /api/workspace-documents/. Все ответы — {ok, status, data}.

export const listDocuments = (workspaceId) =>
  apiFetch('/api/workspace-documents/', { headers: wsHeaders(workspaceId) });

export const updateDocument = (id, body, workspaceId) =>
  apiFetch(`/api/workspace-documents/${id}/`, {
    method: 'PATCH', body, headers: wsHeaders(workspaceId),
  });

export const deleteDocument = (id, workspaceId) =>
  apiFetch(`/api/workspace-documents/${id}/`, { method: 'DELETE', headers: wsHeaders(workspaceId) });

export function createDocument(file, kind, workspaceId) {
  const form = new FormData();
  form.append('file', file);
  if (kind) form.append('kind', kind);
  return apiFetch('/api/workspace-documents/', { method: 'POST', body: form, headers: wsHeaders(workspaceId) });
}

/**
 * Подписаться на SSE-стрим резюмирования документа.
 * Возвращает функцию отписки.
 *
 * Используется fetch + ReadableStream, а не EventSource, потому что
 * EventSource не поддерживает POST и заголовки (включая X-Workspace-Id).
 */
export function streamSummary(id, { onDelta, onStatus, onDone, onError }, workspaceId) {
  const controller = new AbortController();

  (async () => {
    try {
      // CSRF из cookies
      const m = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/);
      const csrf = m ? m[1] : null;
      const headers = { Accept: 'text/event-stream' };
      if (csrf) headers['X-CSRFToken'] = csrf;
      const wsId = workspaceId != null ? workspaceId : getActiveWorkspaceId();
      if (wsId) headers['X-Workspace-Id'] = String(wsId);

      const res = await fetch(`/api/workspace-documents/${id}/summarize/`, {
        method: 'POST',
        headers,
        credentials: 'same-origin',
        signal: controller.signal,
      });
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        let body = null;
        try {
          body = await res.json();
          detail = (body && body.detail) || detail;
        } catch { /* noop */ }
        onError?.(streamError(detail, { ...(body && typeof body === 'object' ? body : {}), status: res.status }));
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let idx;
        while ((idx = buffer.indexOf('\n\n')) !== -1) {
          const frame = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);

          for (const line of frame.split('\n')) {
            if (!line.startsWith('data:')) continue;
            const json = line.slice(5).trim();
            if (!json) continue;
            try {
              const evt = JSON.parse(json);
              if (evt.type === 'delta') onDelta?.(evt.text || '');
              else if (evt.type === 'status') onStatus?.(evt.status);
              else if (evt.type === 'done') onDone?.(evt.document);
              else if (evt.type === 'error') onError?.(streamError(evt.detail || 'stream error', evt));
            } catch (e) {
              // ignore malformed frame
            }
          }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') onError?.(err);
    }
  })();

  return () => controller.abort();
}
